/**
 * The Squares Results tab (ISSUE-1273, screens 17, 18), on the Survivor
 * Results pattern. Standings ranks members and guests by winning squares
 * this season with a breakdown by label. Winners lists every winning square
 * grouped by game, newest first; tapping a row jumps to that square on the
 * Game tab. Filters: All Users, All Games, and the pool's checkpoint chips
 * (hidden when the pool only scores Final). Ranks, counts and every winner
 * are the server's. Nothing here refers to money, payment or gambling.
 */
import {
  useSquaresPoolGet,
  useSquaresResultsGet,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  Card,
  cn,
  FOCUS_RING,
  InlineBanner,
  SegmentedControl,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import { SquaresAddWinnerSheet } from "./SquaresAddWinnerSheet";
import { SquaresChipGroup } from "./SquaresChipGroup";
import {
  SQUARES_CUSTOM_GRID_TAG,
  SQUARES_GAME_ERROR,
  SQUARES_GAME_TRY_AGAIN,
  SQUARES_RESULTS_ADD_WINNER,
  SQUARES_RESULTS_ALL_GAMES,
  SQUARES_RESULTS_ALL_USERS,
  SQUARES_RESULTS_CHIP_ALL,
  SQUARES_RESULTS_CHIPS_LABEL,
  SQUARES_RESULTS_EMPTY_STANDINGS,
  SQUARES_RESULTS_EMPTY_WINNERS,
  SQUARES_RESULTS_GAME_FILTER,
  SQUARES_RESULTS_LOADING,
  SQUARES_RESULTS_STANDINGS,
  SQUARES_RESULTS_USER_FILTER,
  SQUARES_RESULTS_VIEW_LABEL,
  SQUARES_RESULTS_WINNERS,
  SQUARES_UNKNOWN_DIGIT,
  SQUARES_WINNING_UNCLAIMED,
  squaresWinningPendingLine,
  squaresStandingSummary,
  squaresWinCountLabel,
} from "./squaresCopy";
import { requestSquaresFocus } from "./squaresFocusStore";
import {
  squaresEventStatusLine,
  squaresKickoffLabel,
  squaresHasGameScore,
  squaresMatchupLabel,
  squaresScoreLine,
  squaresTeamShort,
} from "./squaresModel";
import type {
  typeSquaresAxes,
  typeSquaresGameGroup,
  typeSquaresResultsData,
  typeSquaresWinnerLabelKey,
} from "./squaresTypes";
import { SquaresGuestTag, squaresSourceNote } from "./SquaresWinningCard";
import {
  type typeSquaresTeam,
  useSquaresLeagueTeams,
} from "./useSquaresLeagueTeams";
import { useSquaresOpenPoolTab } from "./useSquaresOpenPoolTab";

const ALL = "all";

type typeView = "standings" | "winners";

/** What the two digits stand for on one game's grid, for the Winners rows. */
export function squaresResultsAxisNames(input: {
  axes: typeSquaresAxes | null;
  group: Pick<
    typeSquaresGameGroup,
    | "awayTeamId"
    | "homeTeamId"
    | "customTitle"
    | "customTopLabel"
    | "customSideLabel"
  >;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
}): { top: string; side: string } {
  // ISSUE-1282. A custom game carries its own two labels.
  if ((input.group.customTitle ?? null) !== null) {
    return {
      top: input.group.customTopLabel ?? "",
      side: input.group.customSideLabel ?? "",
    };
  }
  const axes = input.axes;
  switch (axes?.kind) {
    case "custom":
      return { top: axes.topLabel ?? "", side: axes.sideLabel ?? "" };
    case "winnerLoser":
      return { top: "Winner", side: "Loser" };
    case "homeAway":
      return { top: "Home", side: "Away" };
    case "teamOpponent":
      return {
        top: squaresTeamShort(input.teamsById, axes.teamId),
        side: "Opponent",
      };
    default:
      return {
        top: squaresTeamShort(input.teamsById, input.group.awayTeamId),
        side: squaresTeamShort(input.teamsById, input.group.homeTeamId),
      };
  }
}

const InitialsMark = (props: { initials: string }): ReactNode => (
  <span
    aria-hidden="true"
    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-caption font-semibold text-ink"
  >
    {props.initials}
  </span>
);

export const SquaresResultsTab = ({
  poolId,
}: typePoolTypePaneProps): ReactNode => {
  const [view, setView] = useState<typeView>("standings");
  const [owner, setOwner] = useState<string>(ALL);
  const [eventId, setEventId] = useState<string>(ALL);
  const [labelKey, setLabelKey] = useState<string>(ALL);
  const [addWinnerFor, setAddWinnerFor] = useState<{
    gridId: string;
    eventId: string;
  } | null>(null);
  const openPoolTab = useSquaresOpenPoolTab();
  const timeZone = resolveViewerTimeZone();

  const poolQuery = useSquaresPoolGet({ poolId });
  const season = poolQuery.data?.data?.season ?? null;
  const canManage = poolQuery.data?.data?.viewer.canManage ?? false;
  const teams = useSquaresLeagueTeams(season?.leagueKey ?? null);

  const [ownerKind, ownerId] =
    owner === ALL ? [null, null] : (owner.split(":") as [string, string]);
  const resultsQuery = useSquaresResultsGet({
    poolId,
    ...(ownerKind === null
      ? {}
      : { ownerKind: ownerKind as "member" | "guest", ownerId }),
    ...(eventId === ALL ? {} : { eventId }),
    ...(labelKey === ALL
      ? {}
      : { labelKey: labelKey as typeSquaresWinnerLabelKey }),
  });
  const results: typeSquaresResultsData | null =
    resultsQuery.data?.data ?? null;

  const formatEvent = (event: {
    awayTeamId: string;
    homeTeamId: string;
  }): string =>
    squaresMatchupLabel({
      awayTeamId: event.awayTeamId,
      homeTeamId: event.homeTeamId,
      teamsById: teams.teamsById,
    });

  const filters =
    results === null ? null : (
      <div
        className="flex flex-col gap-3"
        data-testid="squares-results-filters"
      >
        <div className="grid grid-cols-2 gap-3">
          <Select
            label={SQUARES_RESULTS_USER_FILTER}
            labelHidden
            value={owner}
            items={[
              { value: ALL, label: SQUARES_RESULTS_ALL_USERS },
              ...results.userOptions.map((option) => ({
                value: `${option.ownerKind}:${option.ownerId}`,
                label: option.isGuest
                  ? `${option.displayName} (Guest)`
                  : option.displayName,
              })),
            ]}
            onValueChange={setOwner}
          />
          <Select
            label={SQUARES_RESULTS_GAME_FILTER}
            labelHidden
            value={eventId}
            items={[
              { value: ALL, label: SQUARES_RESULTS_ALL_GAMES },
              ...results.gameOptions.map((option) => ({
                value: option.eventId,
                label: option.label,
              })),
            ]}
            onValueChange={setEventId}
          />
        </div>
        {results.isChipRowShown ? (
          <SquaresChipGroup
            mode="single"
            label={SQUARES_RESULTS_CHIPS_LABEL}
            testId="squares-results-chips"
            value={labelKey}
            options={[
              { value: ALL, label: SQUARES_RESULTS_CHIP_ALL },
              ...results.filterChips.map((chip) => ({
                value: chip.labelKey,
                label: chip.label,
              })),
            ]}
            onChange={setLabelKey}
          />
        ) : null}
      </div>
    );

  const body = ((): ReactNode => {
    if (resultsQuery.isLoading) {
      return (
        <div
          className="flex flex-col gap-2"
          aria-label={SQUARES_RESULTS_LOADING}
          data-testid="squares-results-loading"
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      );
    }
    if (results === null) {
      return (
        <InlineBanner
          variant="warning"
          message={SQUARES_GAME_ERROR}
          action={{
            label: SQUARES_GAME_TRY_AGAIN,
            onAction: () => {
              void resultsQuery.refetch();
            },
          }}
        />
      );
    }
    if (view === "standings") {
      if (results.standings.length === 0) {
        return (
          <Card data-testid="squares-standings-empty">
            <p className="text-body text-ink-secondary">
              {SQUARES_RESULTS_EMPTY_STANDINGS}
            </p>
          </Card>
        );
      }
      return (
        <Card padding="none" data-testid="squares-standings">
          <ol className="flex flex-col divide-y divide-hairline">
            {results.standings.map((row) => (
              <li
                key={`${row.ownerKind}:${row.ownerId}`}
                className="flex items-center gap-3 px-4 py-3"
                data-testid="squares-standing-row"
              >
                <span className="w-6 shrink-0 text-center text-body-strong text-ink">
                  {row.rank}
                </span>
                <InitialsMark initials={row.initials} />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2 text-body-strong text-ink">
                    <span className="truncate">{row.displayName}</span>
                    {row.isGuest ? <SquaresGuestTag /> : null}
                  </span>
                  <span className="text-caption text-ink-secondary">
                    {squaresStandingSummary({
                      breakdown: row.breakdown,
                      squareCount: row.squareCount,
                    })}
                  </span>
                </span>
                <span
                  className="shrink-0 text-h2 text-ink"
                  aria-label={squaresWinCountLabel(row.winCount)}
                >
                  {row.winCount}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      );
    }
    if (results.gameGroups.length === 0) {
      return (
        <Card data-testid="squares-winners-empty">
          <p className="text-body text-ink-secondary">
            {SQUARES_RESULTS_EMPTY_WINNERS}
          </p>
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-4" data-testid="squares-winners">
        {results.gameGroups.map((group) => {
          const axisNames = squaresResultsAxisNames({
            axes: season?.axes ?? null,
            group,
            teamsById: teams.teamsById,
          });
          // ISSUE-1282. A custom game has no teams, kickoff or status.
          const header = (
            group.customTitle !== null && group.customTitle !== undefined
              ? [group.customTitle, SQUARES_CUSTOM_GRID_TAG]
              : [
                  formatEvent(group),
                  group.roundLabel ?? null,
                  squaresKickoffLabel({
                    kickoffAt: group.kickoffAt,
                    timeZone,
                  }),
                  squaresEventStatusLine(group),
                ]
          )
            .filter((part): part is string => part !== null && part !== "")
            .join(" · ");
          return (
            <section
              key={`${group.gridId}:${group.eventId}`}
              className="flex flex-col gap-2"
              data-testid="squares-game-group"
            >
              <h3 className="text-caption font-semibold uppercase text-ink-secondary">
                {header}
              </h3>
              <Card padding="none">
                <ul className="flex flex-col divide-y divide-hairline">
                  {group.winners.map((winner) => {
                    const isUnclaimed =
                      winner.displayName === null ||
                      winner.displayName === undefined;
                    const note = squaresSourceNote(winner);
                    // ISSUE-1273 QA. An admin winner on a game that has not
                    // started has no score to show, never "CHI 0 · SEA 0".
                    const score = squaresHasGameScore(group.status)
                      ? (squaresScoreLine({
                          awayTeamId: group.awayTeamId,
                          homeTeamId: group.homeTeamId,
                          awayScore: winner.awayScore,
                          homeScore: winner.homeScore,
                          teamsById: teams.teamsById,
                        }) ?? "")
                      : "";
                    return (
                      <li key={winner.winnerId}>
                        <button
                          type="button"
                          data-testid="squares-winner-row"
                          className={cn(
                            "flex min-h-touch-target w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-sunken",
                            FOCUS_RING,
                          )}
                          onClick={() => {
                            requestSquaresFocus({
                              poolId,
                              gridId: group.gridId,
                              eventId: group.eventId,
                              squareKey: winner.squareKey ?? null,
                            });
                            openPoolTab("game");
                          }}
                        >
                          {isUnclaimed ? (
                            <InitialsMark initials={SQUARES_UNKNOWN_DIGIT} />
                          ) : (
                            <InitialsMark initials={winner.initials ?? ""} />
                          )}
                          <span className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="flex flex-wrap items-center gap-2 text-body-strong text-ink">
                              <span className="truncate">
                                {isUnclaimed
                                  ? SQUARES_WINNING_UNCLAIMED
                                  : winner.displayName}
                              </span>
                              {winner.isGuest ? <SquaresGuestTag /> : null}
                            </span>
                            <span className="text-caption text-ink-secondary">
                              {[
                                `${axisNames.top} ${String(winner.topDigit)}`,
                                `${axisNames.side} ${String(winner.sideDigit)}`,
                                score,
                              ]
                                .filter((part) => part !== "")
                                .join(" · ")}
                            </span>
                          </span>
                          <span className="flex shrink-0 flex-col items-end gap-1">
                            <span className="rounded-full bg-primary-tint px-2 text-small font-semibold text-primary">
                              {winner.label}
                            </span>
                            {note === null ? null : (
                              <span className="text-small text-ink-secondary">
                                {note}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {group.pendingRows.map((row) => (
                    <li
                      key={`pending-${row.checkpointKey}`}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                      data-testid="squares-winner-pending"
                    >
                      <span className="text-caption font-semibold text-ink-secondary">
                        {row.label}
                      </span>
                      <span className="text-caption text-ink-secondary">
                        {squaresWinningPendingLine(row)}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
              {canManage && group.canAddWinner ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  data-testid="squares-results-add-winner"
                  onClick={() => {
                    setAddWinnerFor({
                      gridId: group.gridId,
                      eventId: group.eventId,
                    });
                  }}
                >
                  {SQUARES_RESULTS_ADD_WINNER}
                </Button>
              ) : null}
            </section>
          );
        })}
      </div>
    );
  })();

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="squares-results-tab"
      data-pool-id={poolId}
    >
      <SegmentedControl
        label={SQUARES_RESULTS_VIEW_LABEL}
        fullWidth
        value={view}
        testId="squares-results-view"
        options={[
          { value: "standings", label: SQUARES_RESULTS_STANDINGS },
          { value: "winners", label: SQUARES_RESULTS_WINNERS },
        ]}
        onValueChange={(next) => {
          setView(next === "winners" ? "winners" : "standings");
        }}
      />
      {filters}
      {body}
      {canManage ? (
        <SquaresAddWinnerSheet
          open={addWinnerFor !== null}
          onClose={() => {
            setAddWinnerFor(null);
          }}
          poolId={poolId}
          gridId={addWinnerFor?.gridId ?? ""}
          eventId={addWinnerFor?.eventId ?? null}
          formatEvent={formatEvent}
        />
      ) : null}
    </div>
  );
};
